/**
 * Travel Locations React Query Hooks
 * Coláiste Lurgan Management System - Travel Location data management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// Query Keys
const QUERY_KEYS = {
  travelLocations: (filters) => ['travelLocations', filters],
  travelLocation: (id) => ['travelLocation', id],
};

// Query invalidation helpers - these need to be called within mutation callbacks
const invalidateQueries = (queryClient) => ({
  travelLocations: () => {
    queryClient.invalidateQueries({ queryKey: ['travelLocations'] });
  },
  travelLocation: (id) => {
    queryClient.invalidateQueries({ queryKey: ['travelLocation', id] });
  },
});

// Main hook for travel locations list with filtering and search
export function useTravelLocations(params = {}, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.travelLocations(params),
    queryFn: () => api.travelLocations.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

// Hook for single travel location
export function useTravelLocation(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.travelLocation(id),
    queryFn: () => api.travelLocations.getById(id),
    enabled: !!id, // Only run if id is provided
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    ...options,
  });
}

// Create travel location mutation
export function useCreateTravelLocation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.travelLocations.create,
    onSuccess: () => {
      invalidateQueries(queryClient).travelLocations();
    },
    ...options,
  });
}

// Update travel location mutation
export function useUpdateTravelLocation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.travelLocations.update(id, data),
    onSuccess: (data, variables) => {
      invalidateQueries(queryClient).travelLocations();
      invalidateQueries(queryClient).travelLocation(variables.id);
    },
    ...options,
  });
}

// Delete travel location mutation
export function useDeleteTravelLocation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.travelLocations.delete,
    onSuccess: () => {
      invalidateQueries(queryClient).travelLocations();
    },
    ...options,
  });
}

// Hook for active travel locations (commonly used for dropdowns)
export function useActiveTravelLocations(options = {}) {
  return useTravelLocations({ is_active: true }, {
    select: (data) => {
      // Handle both array and paginated response formats
      const locations = Array.isArray(data) ? data : (data?.results || []);
      return locations.filter(location => location.is_active);
    },
    ...options,
  });
}

// Hook for travel location analytics (if needed in the future)
export function useTravelLocationAnalytics(options = {}) {
  return useQuery({
    queryKey: ['travelLocationAnalytics'],
    queryFn: () => api.travelLocations.getAnalytics?.() || Promise.resolve({}),
    staleTime: 10 * 60 * 1000, // 10 minutes for analytics
    enabled: !!api.travelLocations.getAnalytics, // Only if endpoint exists
    ...options,
  });
}

// Hook for bulk operations (if needed)
export function useBulkUpdateTravelLocations(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.travelLocations.bulkUpdate || (() => Promise.reject('Not implemented')),
    onSuccess: () => {
      invalidateQueries(queryClient).travelLocations();
    },
    ...options,
  });
}